// src/components/module/ventas/facturas/FacturaCompletaPDF.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image
} from '@react-pdf/renderer';
import { DetalleVenta } from '../../../types/ventas.types';
import { formatMoney, formatDate, formatDateOnly } from '../../../../utils/format';
import { PDFQRCode } from './PDFQRCode';
import { DocumentoElectronico } from '../../../../services/api/facturacionElectronicaApi';
import { contenidoQrDian, cufeLegible } from '../../../../utils/dian';
import { addDays } from 'date-fns';
import { numberToWords } from '../../../../utils/numberToWords';


// Registrar fuentes
Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/helvetica/v6/ka8.woff2' }
  ]
});

const styles = StyleSheet.create({
  page: {
    padding: '15mm',
    fontFamily: 'Helvetica',
    fontSize: 10,
    position: 'relative',  // Para permitir posicionamiento absoluto
    minHeight: '100%',     // Asegurar que la página ocupe todo el alto
  },
  Container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '15mm',
  },
  header: {
    width: '100%',
    marginBottom: '10mm',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: 'white',
    borderRadius: 4,
    padding: '5mm',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerLeft: {
    width: '30%',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
    paddingRight: '5mm',
  },
  headerCenter: {
    width: '35%',
    alignItems: 'center',
    justifyContent: 'center', // Para centrar el QR verticalmente
    paddingHorizontal: '5mm',
  },
  qrCode: {
    width: '100px',
    height: '100px',
  },
  headerRight: {
    width: '30%',
    backgroundColor: '#FAFAFA',
    padding: '5mm',
    borderRadius: 4,
  },
  empresaLogo: {
    width: '100%',
    height: 60,
    objectFit: 'contain',
    marginBottom: '3mm',
  },
  empresaInfo: {
    fontSize: 9,
    color: '#616161',
    marginBottom: '2mm',
  },
  empresaNombre: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#424242',
    marginBottom: '3mm',
  },
  empresaDatos: {
    fontSize: 9,
    color: '#616161',
    marginBottom: '1mm',
  },
  facturaTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1976D2',
    marginBottom: '3mm',
    textAlign: 'right',
  },
  facturaNumero: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: '5mm',
    textAlign: 'right',
    color: '#424242',
  },
  fechasContainer: {
    backgroundColor: '#F5F5F5',
    padding: '3mm',
    borderRadius: 2,
  },
  fechaTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: '2mm',
    color: '#424242',
  },
  fechaText: {
    fontSize: 9,
    color: '#616161',
    marginBottom: '1mm',
  },
  clienteContainer: {
    marginBottom: '5mm',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 4,
    padding: '5mm',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  clienteRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  clienteCol: {
    width: '23%', // Dejamos un pequeño espacio entre columnas
  },
  clienteLabel: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 2,
    fontWeight: 'bold',
  },
  clienteValue: {
    fontSize: 10,
    color: '#333333',
    fontWeight: 'normal',
  },
  tableContainer: {
    marginBottom: '5mm',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    backgroundColor: '#FFFFFF',
    minHeight: '8mm',
  },
  tableRowAlternate: {
    backgroundColor: '#FAFAFA',
  },
  columnHeader: {
    padding: '2mm',
    fontSize: 9,
    fontWeight: 'bold',
    color: '#333333',
  },
  columnContent: {
    padding: '2mm',
    fontSize: 9,
    color: '#444444',
  },
  colItem: {
    width: '15%',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  colDescripcion: {
    width: '37%',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  colCantidad: {
    width: '8%',
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  colPrecio: {
    width: '12%',
    textAlign: 'right',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  colDescuento: {
    width: '8%',
    textAlign: 'right',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  colIva: {
    width: '8%',
    textAlign: 'right',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  colTotal: {
    width: '12%',
    textAlign: 'right',
  },
  moneyText: {
    fontFamily: 'Helvetica',
  },
  totalesContainer: {
    width: '40%',
    alignSelf: 'flex-end',
    marginTop: '5mm',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: '1mm',
  },
  formaPagoContainer: {
    marginTop: '5mm',
  },
  valorLetras: {
    marginTop: '5mm',
    marginBottom: '5mm',
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  totalDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  totalFinal: {              // Nuevo estilo para el total final
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: '2mm',
    backgroundColor: '#e9ecef',  // Fondo más oscuro para el total
    marginTop: '2mm',
    marginBottom: '2mm',
    padding: '2mm',
    fontWeight: 'bold',
  },
  condicionesPago: {
    marginTop: '5mm',
    marginBottom: '5mm',
  },
  observaciones: {
    marginTop: '5mm',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 2,
  },
  footerSection: {
    marginTop: '5mm',
    marginBottom: '35mm', // Agregar margen inferior para el footer
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    paddingTop: '5mm',
  },
  valorLetrasContainer: {
    backgroundColor: '#F8F9FA',
    padding: '2mm',
    marginBottom: '3mm',
    borderRadius: 4,
  },
  valorLetrasText: {
    fontSize: 10,
    color: '#495057',
    fontStyle: 'italic',
  },
  pagoContainer: {
    marginBottom: '3mm',
  },
  pagoColumn: {
    flex: 1,
  },
  pagoTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: '2mm',
  },
  pagoText: {
    fontSize: 10,
    color: '#666666',
  },
  observacionesContainer: {
    backgroundColor: '#F8F9FA',
    padding: '3mm',
    borderRadius: 4,
    marginBottom: '5mm',
  },
  observacionesTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: '2mm',
  },
  observacionesText: {
    fontSize: 10,
    color: '#666666',
    lineHeight: 1.4,
  },
  footer: {
    position: 'absolute', // Posicionar en la parte inferior
    bottom: '10mm', // Ajustar posición
    left: '20mm',
    right: '20mm',
    textAlign: 'justify',
    fontSize: 8,
    color: '#666',
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    marginTop: '10mm',
    paddingTop: '5mm',
  },
  footerWrapper: {
    position: 'absolute',
    bottom: '15mm',        // Igual al padding de la página
    left: '15mm',         // Igual al padding de la página
    right: '15mm',        // Igual al padding de la página
  },
  footerContent: {
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    paddingTop: '5mm',
  },

  cufeContainer: {
    marginTop: 6,
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: '#D0D7DE',
  },
  cufeTitulo: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  cufeValor: {
    fontSize: 6,
    color: '#475569',
    fontFamily: 'Courier',
    marginTop: 1,
  },
  footerText: {
    fontSize: 8,
    color: '#666',
    textAlign: 'justify',
    marginTop: '5mm',
  }
});

interface FacturaCompletaPDFProps {
  venta: DetalleVenta;
  /** Documento electronico validado por la DIAN (si la venta se facturo). */
  documentoElectronico?: DocumentoElectronico | null;
  configuracionEmpresa: {
    nombre: string;
    nit: string;
    direccion: string;
    telefono: string;
    email: string;
    sitioWeb?: string;
    logo?: string;
  };
}

const getFechas = (fechaExpedicion: string | Date) => {
  const fechaGeneracion = new Date(); // Fecha actual
  const fechaExp = new Date(fechaExpedicion);
  const fechaVencimiento = addDays(fechaExp, 15);

  return {
    generacion: fechaGeneracion,
    expedicion: fechaExp,
    vencimiento: fechaVencimiento
  };
};

export const FacturaCompletaPDF: React.FC<FacturaCompletaPDFProps> = ({
  venta,
  configuracionEmpresa,
  documentoElectronico
}) => {
  if (!venta || !configuracionEmpresa) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Error: No hay datos para mostrar</Text>
        </Page>
      </Document>
    );
  }
  const fechas = getFechas(venta.fecha);

  return (

    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              {configuracionEmpresa.logo ? (
                <Image
                  src={`${process.env.REACT_APP_API_URL}${configuracionEmpresa.logo}`}
                  style={styles.empresaLogo}
                />
              ) : null}
              <Text style={styles.empresaNombre}>{configuracionEmpresa.nombre}</Text>
              {configuracionEmpresa.nit && (
                <Text style={styles.empresaDatos}>NIT: {configuracionEmpresa.nit}</Text>
              )}
              {configuracionEmpresa.direccion && (
                <Text style={styles.empresaDatos}>{configuracionEmpresa.direccion}</Text>
              )}
              {configuracionEmpresa.telefono && (
                <Text style={styles.empresaDatos}>Tel: {configuracionEmpresa.telefono}</Text>
              )}
              {configuracionEmpresa.email && (
                <Text style={styles.empresaDatos}>{configuracionEmpresa.email}</Text>
              )}
            </View>

            <View style={styles.headerCenter}>
              <PDFQRCode
                value={
                  contenidoQrDian(documentoElectronico) ??
                  `${configuracionEmpresa.nombre}
NIT: ${configuracionEmpresa.nit}
Factura N°: ${venta.numeroFactura}`
                }
                size={100}
              />
            </View>

            <View style={styles.headerRight}>
              <Text style={styles.facturaTitle}>Factura venta</Text>
              <Text style={styles.facturaNumero}>No. {venta.numeroFactura}</Text>

              <View style={styles.fechasContainer}>
                <Text style={styles.fechaTitle}>Fecha Factura</Text>
                <Text style={styles.fechaText}>
                  Generación: {formatDateOnly(fechas.generacion)}
                </Text>
                <Text style={styles.fechaText}>
                  Expedición: {formatDateOnly(fechas.expedicion)}
                </Text>
                <Text style={styles.fechaText}>
                  Vencimiento: {formatDateOnly(fechas.vencimiento)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Información del Cliente */}
        <View style={styles.clienteContainer}>
          <View style={styles.clienteRow}>
            <View style={styles.clienteCol}>
              <Text style={styles.clienteLabel}>Señores</Text>
              <Text style={styles.clienteValue}>{venta.cliente.nombre}</Text>
            </View>

            <View style={styles.clienteCol}>
              <Text style={styles.clienteLabel}>NIT</Text>
              <Text style={styles.clienteValue}>{venta.cliente.identificacion}</Text>
            </View>

            <View style={styles.clienteCol}>
              <Text style={styles.clienteLabel}>Teléfono</Text>
              <Text style={styles.clienteValue}>{venta.cliente.telefono}</Text>
            </View>

            <View style={styles.clienteCol}>
              <Text style={styles.clienteLabel}>Ciudad</Text>
              <Text style={styles.clienteValue}>{venta.cliente.direccion}</Text>
            </View>
          </View>
        </View>

        {/* Tabla de Productos */}
        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.columnHeader, styles.colItem]}>Código</Text>
            <Text style={[styles.columnHeader, styles.colDescripcion]}>Descripción</Text>
            <Text style={[styles.columnHeader, styles.colCantidad]}>Cant.</Text>
            <Text style={[styles.columnHeader, styles.colPrecio]}>Precio Unit.</Text>
            <Text style={[styles.columnHeader, styles.colDescuento]}>Desc.</Text>
            <Text style={[styles.columnHeader, styles.colIva]}>IVA</Text>
            <Text style={[styles.columnHeader, styles.colTotal]}>Total</Text>
          </View>

          {venta.articulos.map((articulo, index) => {
            const subtotal = articulo.Cantidad * articulo.PrecioVenta;
            const descuentoValor = subtotal * (articulo.Descuento / 100);
            const subtotalConDescuento = subtotal - descuentoValor;
            const ivaTotal = articulo.Impuesto.reduce((sum, imp) =>
              sum + (subtotalConDescuento * (imp.Valor / 100)), 0);

            return (
              <View key={index} style={[
                styles.tableRow,
                index % 2 === 1 ? styles.tableRowAlternate : {}
              ]}>
                <Text style={[styles.columnContent, styles.colItem]}>{articulo.EAN}</Text>
                <Text style={[styles.columnContent, styles.colDescripcion]}>
                  {articulo.Nombre}
                </Text>
                <Text style={[styles.columnContent, styles.colCantidad]}>
                  {articulo.Cantidad}
                </Text>
                <Text style={[styles.columnContent, styles.colPrecio, styles.moneyText]}>
                  {formatMoney(articulo.PrecioVenta)}
                </Text>
                <Text style={[styles.columnContent, styles.colDescuento]}>
                  {articulo.Descuento > 0 ? `${articulo.Descuento}%` : '-'}
                </Text>
                <Text style={[styles.columnContent, styles.colIva]}>
                  {articulo.Impuesto[0]?.Valor || 0}%
                </Text>
                <Text style={[styles.columnContent, styles.colTotal, styles.moneyText]}>
                  {formatMoney(articulo.Valor)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Totales */}
        <View style={styles.totalesContainer}>
          <View style={styles.totalRow}>
            <Text>Total Bruto:</Text>
            <Text>{formatMoney(venta.subtotal)}</Text>
          </View>
          {venta.descuentoTotal > 0 && (
            <View style={styles.totalRow}>
              <Text>Descuento:</Text>
              <Text> - {formatMoney(venta.descuentoTotal)}</Text>
            </View>
          )}
          {venta.impuestos.map((impuesto, index) => (
            <View key={index} style={styles.totalRow}>
              <Text>{impuesto.nombre}:</Text>
              <Text>{formatMoney(impuesto.valor)}</Text>
            </View>
          ))}

          <View style={styles.totalDivider} />

          <View style={styles.totalFinal}>
            <Text>Total a Pagar:</Text>
            <Text>{formatMoney(venta.totalAPagar)}</Text>
          </View>
        </View>

        <View style={styles.footerWrapper}>
          <View style={styles.footerContent}>
            <View style={styles.valorLetrasContainer}>
              <Text style={styles.valorLetrasText}>
                Son: {numberToWords(venta.totalAPagar)}
              </Text>
            </View>

            <View style={styles.pagoContainer}>
              <Text style={styles.pagoTitle}>Forma de Pago: {venta.tipoPago.nombre}</Text>
            </View>

            {documentoElectronico?.CUFE && (
              <View style={styles.cufeContainer}>
                <Text style={styles.cufeTitulo}>
                  CUFE{documentoElectronico.NumeroResolucion
                    ? ` · Resolución DIAN ${documentoElectronico.NumeroResolucion}`
                    : ''}
                </Text>
                <Text style={styles.cufeValor}>{cufeLegible(documentoElectronico.CUFE)}</Text>
              </View>
            )}

            <Text style={styles.footerText}>
              A esta factura de venta aplican las normas relativas a la letra de cambio (artículo 5 Ley 1231 de 2008).
              Con esta el Comprador declara haber recibido real y materialmente las mercancías o prestación de servicios descritos en este título.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default FacturaCompletaPDF;